/** PLAY FROM ANYWHERE — unsubscribe. One tap from the email, no sign-in.
 *
 *  The link in a venue's email carries ?sender=venue, the one in a Tap 'N Win
 *  Sports email carries ?sender=tapelligence: that sender alone is stopped, at
 *  once. "Stop every email" is still one more tap away, and an older link with
 *  no sender stops everything exactly as it always did. Points and the account
 *  are untouched either way.
 */
import { useCallback, useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Loader2, MailX } from 'lucide-react';

const API = `${(typeof window !== 'undefined' ? window.location.origin : '')}/api/goal-remote/public/unsubscribe`;

export default function RemoteUnsubscribe() {
    const { token } = useParams();
    const [params] = useSearchParams();
    const sender = params.get('sender') || '';
    const { t } = useTranslation();
    const [done, setDone] = useState(null);
    const [all, setAll] = useState(false);
    const [failed, setFailed] = useState(false);
    const [venueName, setVenueName] = useState('');

    useEffect(() => {
        (async () => {
            try {
                const res = await fetch(`${API}/${token}/senders`);
                const data = await res.json();
                const venue = (data.senders || []).find((s) => s.sender === 'venue');
                if (venue?.venue_name) setVenueName(venue.venue_name);
            } catch (_e) { /* the name is a nicety, not a blocker */ }
        })();
    }, [token]);

    const stop = useCallback(async (which) => {
        try {
            const url = which ? `${API}/${token}?sender=${which}` : `${API}/${token}`;
            const res = await fetch(url, { method: 'POST' });
            if (!res.ok) throw new Error('unsub');
            setDone(await res.json());
            setAll(!which);
        } catch (_e) { setFailed(true); }
    }, [token]);

    useEffect(() => { stop(sender); }, [stop, sender]);

    return (
        <div className="min-h-screen bg-slate-950 text-white flex items-center justify-center px-6"
             data-testid="remote-unsub-screen">
            <div className="w-full max-w-sm text-center">
                {failed ? (
                    <p className="text-sm opacity-80" data-testid="remote-unsub-failed">
                        {t('goalGame.remoteUnsubFailed')}
                    </p>
                ) : !done ? (
                    <Loader2 className="animate-spin mx-auto opacity-50" size={22}
                             data-testid="remote-unsub-loading" />
                ) : (
                    <>
                        <MailX className="mx-auto mb-4 text-amber-400" size={30} />
                        <h1 className="text-2xl font-black mb-3" data-testid="remote-unsub-title">
                            {t('goalGame.remoteUnsubTitle')}
                        </h1>
                        <p className="text-sm opacity-80 mb-2" data-testid="remote-unsub-done">
                            {all
                                ? t('goalGame.remoteUnsubDone', { email: done.email_masked })
                                : t('goalGame.remoteUnsubOneSender', {
                                    email: done.email_masked,
                                    sender: sender === 'tapelligence'
                                        ? t('goalGame.senderTapelligence')
                                        : venueName || t('goalGame.senderVenue'),
                                })}
                        </p>
                        {!all && (
                            <button type="button" onClick={() => stop('')}
                                    className="mt-3 w-full rounded-xl border border-white/20 py-2.5 text-sm font-bold hover:bg-white/10"
                                    data-testid="remote-unsub-all">
                                {t('goalGame.remoteUnsubAll')}
                            </button>
                        )}
                        <p className="text-xs opacity-55 mt-4" data-testid="remote-unsub-keep">
                            {t('goalGame.remoteUnsubKeep')}
                        </p>
                    </>
                )}
            </div>
        </div>
    );
}
